import React, { useState } from "react";
import { Box, Popover, Select, UnstyledButton } from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import sx from "../../sales/shared/sales.module.css";
import {
  SalesSearchInput,
  TextToken,
  ResultCount,
} from "../../sales/shared/salesToolbarParts";
import { fieldStyles } from "./fieldStyles";

export interface GroupFilters {
  type: "all" | "dynamic" | "static";
  size: "any" | "over100" | "under100" | "empty";
  updated: "any" | "7d" | "30d";
}

export const DEFAULT_FILTERS: GroupFilters = {
  type: "all",
  size: "any",
  updated: "any",
};

const TYPE_LABELS: Record<GroupFilters["type"], string> = {
  all: "All types",
  dynamic: "Dynamic",
  static: "Static",
};
const SIZE_LABELS: Record<GroupFilters["size"], string> = {
  any: "Any size",
  over100: "Over 100 members",
  under100: "Under 100 members",
  empty: "Empty",
};
const UPDATED_LABELS: Record<GroupFilters["updated"], string> = {
  any: "Any time",
  "7d": "Last 7 days",
  "30d": "Last 30 days",
};

interface Props {
  query: string;
  onQueryChange: (q: string) => void;
  filters: GroupFilters;
  onFiltersChange: (f: GroupFilters) => void;
  count: number;
  total: number;
}

export default function ClientGroupsToolbar({
  query,
  onQueryChange,
  filters,
  onFiltersChange,
  count,
  total,
}: Props) {
  const [open, setOpen] = useState(false);
  const set = (patch: Partial<GroupFilters>) =>
    onFiltersChange({ ...filters, ...patch });

  const anyApplied =
    filters.type !== "all" ||
    filters.size !== "any" ||
    filters.updated !== "any";

  const field = (label: string, node: React.ReactNode, last?: boolean) => (
    <Box mb={last ? 0 : 13}>
      <Box className={sx.fieldLabel}>{label}</Box>
      {node}
    </Box>
  );

  return (
    <Box className={sx.toolbar}>
      <SalesSearchInput
        value={query}
        onChange={onQueryChange}
        placeholder="Search groups"
      />

      {filters.type !== "all" && (
        <TextToken
          label="Type"
          value={TYPE_LABELS[filters.type]}
          onEdit={() => setOpen(true)}
          onRemove={() => set({ type: "all" })}
        />
      )}
      {filters.size !== "any" && (
        <TextToken
          label="Size"
          value={SIZE_LABELS[filters.size]}
          onEdit={() => setOpen(true)}
          onRemove={() => set({ size: "any" })}
        />
      )}
      {filters.updated !== "any" && (
        <TextToken
          label="Updated"
          value={UPDATED_LABELS[filters.updated]}
          onEdit={() => setOpen(true)}
          onRemove={() => set({ updated: "any" })}
        />
      )}

      <Popover
        opened={open}
        onChange={setOpen}
        width={248}
        position="bottom-start"
        shadow="md"
        withArrow={false}
      >
        <Popover.Target>
          <UnstyledButton
            className={`${sx.filterTrigger} ${
              anyApplied ? sx.filterTriggerActive : ""
            }`}
            onClick={() => setOpen((o) => !o)}
          >
            <IconPlus size={13} stroke={2.4} />
            Filter
          </UnstyledButton>
        </Popover.Target>
        <Popover.Dropdown p={16}>
          {field(
            "Type",
            <Select
              styles={fieldStyles}
              data={Object.entries(TYPE_LABELS).map(([value, label]) => ({
                value,
                label,
              }))}
              value={filters.type}
              onChange={(v) =>
                set({ type: (v as GroupFilters["type"]) ?? "all" })
              }
              allowDeselect={false}
            />,
          )}
          {field(
            "Size",
            <Select
              styles={fieldStyles}
              data={Object.entries(SIZE_LABELS).map(([value, label]) => ({
                value,
                label,
              }))}
              value={filters.size}
              onChange={(v) =>
                set({ size: (v as GroupFilters["size"]) ?? "any" })
              }
              allowDeselect={false}
            />,
          )}
          {field(
            "Updated",
            <Select
              styles={fieldStyles}
              data={Object.entries(UPDATED_LABELS).map(([value, label]) => ({
                value,
                label,
              }))}
              value={filters.updated}
              onChange={(v) =>
                set({ updated: (v as GroupFilters["updated"]) ?? "any" })
              }
              allowDeselect={false}
            />,
            true,
          )}
        </Popover.Dropdown>
      </Popover>

      <Box style={{ marginLeft: "auto" }}>
        <ResultCount count={count} total={total} itemLabel="groups" />
      </Box>
    </Box>
  );
}
